package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.AttendanceDto
import se.mittpass.shared.api.AuditLogEntryDto
import se.mittpass.shared.api.SnapshotEntryDto
import se.mittpass.shared.api.UpdateAttendanceRequest

private val scope = MainScope()

private enum class PliggarViewMode { DAG, VECKA, MANAD, AR }

@Composable
fun PersonalliggarePage(workplaceId: Long) {
    var viewMode by remember { mutableStateOf(PliggarViewMode.DAG) }
    var anchor by remember { mutableStateOf(todayLocalDate()) }
    var showSnapshot by remember { mutableStateOf(false) }
    var attendance by remember { mutableStateOf<List<AttendanceDto>>(emptyList()) }
    var rangeData by remember { mutableStateOf<Map<String, List<AttendanceDto>>>(emptyMap()) }
    var snapshot by remember { mutableStateOf<List<SnapshotEntryDto>?>(null) }
    var loading by remember { mutableStateOf(false) }
    var editingRecord by remember { mutableStateOf<AttendanceDto?>(null) }
    var auditLog by remember { mutableStateOf<List<AuditLogEntryDto>?>(null) }
    var exportFrom by remember { mutableStateOf(defaultExportFrom()) }
    var exportTo by remember { mutableStateOf(todayLocalDate()) }
    var exportError by remember { mutableStateOf<String?>(null) }
    var windowWidth by remember { mutableStateOf(kotlinx.browser.window.innerWidth) }
    val isMobile = windowWidth < 768
    val isNarrow = windowWidth < 1024

    DisposableEffect(Unit) {
        val handler: (dynamic) -> Unit = { windowWidth = kotlinx.browser.window.innerWidth }
        kotlinx.browser.window.addEventListener("resize", handler)
        onDispose { kotlinx.browser.window.removeEventListener("resize", handler) }
    }

    fun loadData() {
        loading = true
        scope.launch {
            when (viewMode) {
                PliggarViewMode.DAG -> {
                    attendanceRepo.getAttendance(workplaceId, anchor).onSuccess {
                        attendance = it; rangeData = emptyMap()
                    }
                }
                PliggarViewMode.VECKA -> {
                    val ws = mondayOf(anchor)
                    val we = addDays(ws, 6)
                    attendanceRepo.getAttendanceRange(workplaceId, ws, we).onSuccess { list ->
                        rangeData = list.groupBy { it.checkInTime.substringBefore("T") }
                    }
                }
                PliggarViewMode.MANAD -> {
                    val (from, to) = monthGridRange(anchor)
                    attendanceRepo.getAttendanceRange(workplaceId, from, to).onSuccess { list ->
                        rangeData = list.groupBy { it.checkInTime.substringBefore("T") }
                    }
                }
                PliggarViewMode.AR -> {
                    val year = anchor.take(4)
                    attendanceRepo.getAttendanceRange(workplaceId, "$year-01-01", "$year-12-31").onSuccess { list ->
                        rangeData = list.groupBy { it.checkInTime.substringBefore("T") }
                    }
                }
            }
            loading = false
        }
    }

    LaunchedEffect(workplaceId, viewMode, anchor) { loadData() }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {

        // Skatteverket Compliance Header
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border", "1px solid var(--rp-hl-med)")
                property("border-left", "4px solid var(--rp-pine)")
                property("border-radius", "16px")
                property("padding", "0.85rem 1.25rem")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("flex-wrap", "wrap")
                property("gap", "0.75rem")
            }
        }) {
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem") } }) {
                FaIcon("fa-solid fa-shield-halved", size = "1.1rem", color = "var(--rp-pine)")
                Div {
                    Span({ style { property("display", "block"); property("font-weight", "800"); property("font-size", "0.9rem"); property("color", "var(--rp-text)") } }) {
                        Text("Elektronisk personalliggare (SKVFS 2015:6 / Skatteförfarandelagen)")
                    }
                    Span({ style { property("display", "block"); property("font-size", "0.78rem"); property("color", "var(--rp-subtle)") } }) {
                        Text("Godkänd för oannonserad kontroll av Skatteverket vid café- och restaurangverksamhet.")
                    }
                }
            }
            Span({
                style {
                    property("background", "var(--rp-ok-bg)")
                    property("color", "var(--rp-ok-text)")
                    property("border", "1px solid var(--rp-ok-border)")
                    property("padding", "0.25rem 0.65rem")
                    property("border-radius", "50px")
                    property("font-size", "0.75rem")
                    property("font-weight", "700")
                }
            }) { Text("Skatteverket-redo") }
        }

        // Toolbar card
        Div({ style {
            property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.25rem")
            property("border", "1px solid var(--rp-hl-med)")
        } }) {
            Div({ style {
                property("display", "flex"); property("align-items", "center")
                property("gap", if (isMobile) "0.5rem" else "0.75rem")
                property("flex-wrap", "wrap")
            } }) {
                // Left: prev/today/next
                Div({ style {
                    property("display", "flex"); property("align-items", "center"); property("gap", "0.4rem")
                    property("background", "var(--rp-surface)"); property("border", "1px solid var(--rp-overlay)")
                    property("border-radius", "12px"); property("padding", "0.25rem")
                } }) {
                    ToolbarIconBtn("fa-solid fa-chevron-left", "Föregående") { stepAnchor(viewMode, anchor, false) { anchor = it } }
                    ToolbarTextBtn("Idag") { anchor = todayLocalDate() }
                    ToolbarIconBtn("fa-solid fa-chevron-right", "Nästa") { stepAnchor(viewMode, anchor, true) { anchor = it } }
                }

                // Center: range label
                Span({ style {
                    property("font-size", if (isMobile) "0.95rem" else "1.1rem")
                    property("font-weight", "700"); property("color", "var(--rp-text)")
                    property("flex", if (isMobile) "1 1 100%" else "1")
                    property("text-align", if (isMobile) "left" else "center")
                    property("padding", if (isMobile) "0.25rem 0.25rem 0" else "0 0.5rem")
                } }) { Text(pliggarCenterLabel(viewMode, anchor)) }

                // Right: view segmented + snapshot toggle
                Div({ style {
                    property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem")
                } }) {
                    PliggarViewSegmented(viewMode, isMobile) { viewMode = it; showSnapshot = false }
                    Button({
                        onClick {
                            showSnapshot = !showSnapshot
                            if (showSnapshot && snapshot == null) {
                                scope.launch { attendanceRepo.getSnapshot(workplaceId).onSuccess { snapshot = it } }
                            }
                        }
                        attr("title", "Snapshot (nu)")
                        style {
                            property("background", if (showSnapshot) "var(--rp-pine)" else "var(--rp-surface)")
                            property("border", "1px solid var(--rp-hl-med)"); property("border-radius", "12px")
                            property("width", "40px"); property("height", "40px"); property("cursor", "pointer")
                            property("color", if (showSnapshot) "#FFFFFF" else "var(--rp-subtle)")
                            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                        }
                    }) { FaIcon("fa-solid fa-bolt", size = "0.9rem") }
                }
            }
        }

        if (showSnapshot) {
            // Snapshot view
            Div({ style { cardStyle() } }) {
                H3({ style { headingStyle() } }) { Text("Närvarande just nu") }
                snapshot?.let { entries ->
                    if (entries.isEmpty()) {
                        Div({ style { property("text-align","center"); property("padding","2rem"); property("color","var(--rp-muted)") } }) { Text("Ingen personal incheckad.") }
                    } else {
                        Div({ style { gridHeaderStyle("1fr 1fr 1fr 120px") } }) {
                            Span { Text("Namn") }; Span { Text("Personnummer") }; Span { Text("Incheckad") }; Span { }
                        }
                        entries.forEach { e ->
                            val name = listOfNotNull(e.firstName, e.lastName).joinToString(" ").ifEmpty { e.email }
                            Div({ style { gridRowStyle("1fr 1fr 1fr 120px") } }) {
                                Span({ style { property("font-weight","600") } }) { Text(name) }
                                Span({ style { property("color","var(--rp-subtle)") } }) { Text(e.personalNumber ?: "–") }
                                Span({ style { property("color","var(--rp-ok-text)"); property("font-weight","600") } }) { Text(e.checkInTime.take(16).replace("T"," ")) }
                                Span { }
                            }
                        }
                        Div({ style { property("margin-top","1rem"); property("font-weight","700"); property("color","var(--rp-text)") } }) {
                            Text("Totalt: ${entries.size} person(er)")
                        }
                    }
                } ?: Div({ style { property("text-align","center"); property("padding","2rem"); property("color","var(--rp-subtle)") } }) { Text("Laddar…") }
            }
        } else {
            // Main view
            Div({ style { cardStyle() } }) {
                H3({ style { headingStyle() } }) { Text("Personalliggare – ${pliggarCenterLabel(viewMode, anchor)}") }
                if (loading) {
                    Div({ style { property("text-align","center"); property("padding","2rem"); property("color","var(--rp-subtle)") } }) { Text("Laddar…") }
                } else {
                    when (viewMode) {
                        PliggarViewMode.DAG -> {
                            if (attendance.isEmpty()) {
                                Div({ style { property("text-align","center"); property("padding","2rem"); property("color","var(--rp-subtle)") } }) {
                                    Text("Inga registreringar denna dag.")
                                }
                            } else {
                                Div({ style { gridHeaderStyle("minmax(180px, 1.2fr) minmax(130px, 1fr) 90px 90px 170px") } }) {
                                    Span { Text("Namn") }; Span { Text("Personnr") }; Span { Text("In") }; Span { Text("Ut") }
                                    Span({ style { property("text-align", "right"); property("padding-right", "0.5rem") } }) { Text("Åtgärder") }
                                }
                                attendance.forEach { rec ->
                                    val name = listOfNotNull(rec.firstName, rec.lastName).joinToString(" ").ifEmpty { rec.userEmail }
                                    val inTime = rec.checkInTime.let { if (it.contains("T")) it.substringAfter("T").take(5) else it.take(5) }
                                    val recDate = rec.checkInTime.substringBefore("T")
                                    val isToday = recDate == todayLocalDate()
                                    val isOngoing = rec.checkOutTime == null && isToday
                                    val missingOut = rec.checkOutTime == null && !isToday
                                    val outTime = rec.checkOutTime?.let { if (it.contains("T")) it.substringAfter("T").take(5) else it.take(5) } ?: (if (isOngoing) "Pågående" else "–")
                                    Div({
                                        style {
                                            gridRowStyle("minmax(180px, 1.2fr) minmax(130px, 1fr) 90px 90px 170px")
                                            if (missingOut) { property("background","var(--rp-err-bg)"); property("border-radius","10px"); property("border-left","4px solid var(--rp-err-border)") }
                                            else if (isOngoing) { property("background","var(--rp-ok-bg)"); property("border-radius","10px"); property("border-left","4px solid var(--rp-ok-border)") }
                                        }
                                    }) {
                                        Span({ style { property("font-weight","600") } }) {
                                            Text(name)
                                            if (isOngoing) {
                                                Span({ style { property("margin-left","0.5rem"); property("font-size","0.75rem"); property("background","var(--rp-ok-text)"); property("color","white"); property("padding","0.15rem 0.45rem"); property("border-radius","6px"); property("font-weight","700") } }) { Text("Pågående pass") }
                                            } else if (missingOut) {
                                                Span({ style { property("margin-left","0.5rem"); property("font-size","0.75rem"); property("background","var(--rp-err-border)"); property("color","white"); property("padding","0.15rem 0.45rem"); property("border-radius","6px"); property("font-weight","700") } }) { Text("Saknar utcheckning") }
                                            }
                                        }
                                        Span({ style { property("color","var(--rp-subtle)"); property("font-size","0.9rem") } }) { Text(rec.personalNumber ?: "–") }
                                        Span({ style { property("color","var(--rp-foam)"); property("font-weight","700") } }) { Text(inTime) }
                                        Span({ style { property("color", if (missingOut) "var(--rp-err-text)" else if (isOngoing) "var(--rp-ok-text)" else "var(--rp-text)"); property("font-weight","600") } }) { Text(outTime) }

                                        Div({ style { property("display", "flex"); property("align-items", "center"); property("justify-content", "flex-end"); property("gap", "0.4rem") } }) {
                                            Button({
                                                onClick { editingRecord = rec }
                                                attr("title", "Rätta tid")
                                                style {
                                                    property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.35rem")
                                                    property("padding", "0.35rem 0.65rem"); property("border", "1px solid var(--rp-hl-med)")
                                                    property("background", "var(--rp-surface)"); property("color", "var(--rp-pine)")
                                                    property("border-radius", "8px"); property("font-weight", "600"); property("font-size", "0.78rem")
                                                    property("cursor", "pointer"); property("font-family", "inherit")
                                                    property("transition", "all 0.15s ease"); property("box-shadow", "0 1px 2px rgba(0,0,0,0.04)")
                                                }
                                            }) {
                                                FaIcon("fa-solid fa-pen-to-square", size = "0.75rem", color = "var(--rp-pine)")
                                                Text("Rätta")
                                            }

                                            Button({
                                                onClick { scope.launch { attendanceRepo.getAuditLog(rec.id).onSuccess { auditLog = it } } }
                                                attr("title", "Visa ändringshistorik")
                                                style {
                                                    property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.35rem")
                                                    property("padding", "0.35rem 0.65rem"); property("border", "1px solid var(--rp-hl-med)")
                                                    property("background", "var(--rp-surface)"); property("color", "var(--rp-subtle)")
                                                    property("border-radius", "8px"); property("font-weight", "600"); property("font-size", "0.78rem")
                                                    property("cursor", "pointer"); property("font-family", "inherit")
                                                    property("transition", "all 0.15s ease"); property("box-shadow", "0 1px 2px rgba(0,0,0,0.04)")
                                                }
                                            }) {
                                                FaIcon("fa-solid fa-clock-rotate-left", size = "0.75rem", color = "var(--rp-subtle)")
                                                Text("Historik")
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        PliggarViewMode.VECKA -> PliggarWeekView(mondayOf(anchor), rangeData) { editingRecord = it }
                        PliggarViewMode.MANAD -> PliggarMonthView(anchor, rangeData, isMobile) { day ->
                            anchor = day; viewMode = PliggarViewMode.DAG
                        }
                        PliggarViewMode.AR -> PliggarYearView(anchor.take(4).toInt(), rangeData.values.flatten(), isMobile, isNarrow) { day ->
                            anchor = day; viewMode = PliggarViewMode.MANAD
                        }
                    }
                }
            }

            // Export section (only in non-snapshot views)
            Div({ style { cardStyle() } }) {
                H3({ style { headingStyle() } }) { Text("Exportera") }
                exportError?.let { err ->
                    Div({ style {
                        property("background","var(--rp-err-bg)"); property("border","1px solid var(--rp-err-border)")
                        property("border-radius","8px"); property("padding","0.75rem 1rem")
                        property("color","var(--rp-err-text)"); property("font-size","0.85rem")
                        property("margin-bottom","0.75rem"); property("font-weight","600")
                    } }) {
                        FaIcon("fa-solid fa-triangle-exclamation", size = "0.85rem", color = "var(--rp-err-text)")
                        Text(" $err")
                    }
                }
                Div({ style { property("display","flex"); property("align-items","center"); property("gap","0.75rem"); property("flex-wrap","wrap") } }) {
                    Span({ style { property("font-weight","600"); property("color","var(--rp-subtle)"); property("font-size","0.85rem") } }) { Text("Från") }
                    Input(InputType.Date) { value(exportFrom); onInput { exportFrom = it.value; exportError = null }; style { inputStyle() } }
                    Span({ style { property("font-weight","600"); property("color","var(--rp-subtle)"); property("font-size","0.85rem") } }) { Text("Till") }
                    Input(InputType.Date) { value(exportTo); onInput { exportTo = it.value; exportError = null }; style { inputStyle() } }
                    Span({ style { property("font-size","0.8rem"); property("color","var(--rp-muted)"); property("width","100%") } }) {
                        Text("Standard: ca två år bakåt till idag. Justera vid behov för importerad historik.")
                    }
                    Button({
                        onClick {
                            scope.launch {
                                exportError = null
                                attendanceRepo.exportCsv(workplaceId, exportFrom, exportTo)
                                    .onSuccess { csv -> downloadFile("personalliggare_${exportFrom}_${exportTo}.csv", csv, "text/csv") }
                                    .onFailure { e -> exportError = e.message ?: "Export misslyckades" }
                            }
                        }
                        style {
                            property("padding","0.6rem 1.2rem"); property("border","none")
                            property("background","linear-gradient(135deg, var(--rp-pine), var(--rp-foam))"); property("color","white")
                            property("border-radius","50px"); property("font-weight","700"); property("font-size","0.85rem"); property("cursor","pointer")
                        }


                    }) {
                        FaIcon("fa-solid fa-download", size = "0.8rem", color = "white")
                        Text(" CSV")
                    }

                }
            }
        }
    }

    // Edit modal
    editingRecord?.let { rec ->
        var editIn by remember(rec.id) { mutableStateOf(rec.checkInTime.let { if (it.contains("T")) it.substringAfter("T").take(5) else it.take(5) }) }
        var editOut by remember(rec.id) { mutableStateOf(rec.checkOutTime?.let { if (it.contains("T")) it.substringAfter("T").take(5) else it.take(5) } ?: "") }
        Div({ onClick { editingRecord = null }; style { overlayStyle() } }) {
            Div({ onClick { it.stopPropagation() }; style { modalCardStyle() } }) {
                H3({ style { headingStyle() } }) { Text("Rätta tid") }
                Span({ style { property("color","var(--rp-subtle)"); property("font-size","0.9rem"); property("display","block"); property("margin-bottom","1rem") } }) {
                    Text(listOfNotNull(rec.firstName, rec.lastName).joinToString(" ").ifEmpty { rec.userEmail })
                }
                Div({ style { property("display","flex"); property("flex-direction","column"); property("gap","1rem") } }) {
                    Div { Span({ style { labelStyle() } }) { Text("Incheckning") }; Input(InputType.Time) { value(editIn); onInput { editIn = it.value }; style { inputStyle() } } }
                    Div { Span({ style { labelStyle() } }) { Text("Utcheckning") }; Input(InputType.Time) { value(editOut); onInput { editOut = it.value }; style { inputStyle() } } }
                }
                Div({ style { property("display","flex"); property("gap","0.75rem"); property("justify-content","flex-end"); property("margin-top","1.5rem") } }) {
                    Button({ onClick { editingRecord = null }; style { property("padding","0.75rem 1.5rem"); property("border","2px solid var(--rp-pine)"); property("background","transparent"); property("color","var(--rp-pine)"); property("border-radius","50px"); property("font-weight","700"); property("cursor","pointer") } }) { Text("Avbryt") }
                    Button({
                        onClick {
                            val datePrefix = rec.checkInTime.substringBefore("T")
                            val newIn = if (editIn.isNotBlank()) "${datePrefix}T${editIn}:00" else null
                            val newOut = if (editOut.isNotBlank()) "${datePrefix}T${editOut}:00" else null
                            scope.launch {
                                attendanceRepo.updateAttendance(rec.id, UpdateAttendanceRequest(checkInTime = newIn, checkOutTime = newOut))
                                loadData()
                                editingRecord = null
                            }
                        }
                        style { property("padding","0.75rem 1.5rem"); property("border","none"); property("background","linear-gradient(135deg, var(--rp-pine), var(--rp-iris))"); property("color","white"); property("border-radius","50px"); property("font-weight","700"); property("cursor","pointer") }
                    }) { Text("Spara") }
                }
            }
        }
    }

    // Audit log modal
    auditLog?.let { entries ->
        Div({ onClick { auditLog = null }; style { overlayStyle() } }) {
            Div({ onClick { it.stopPropagation() }; style { modalCardStyle() } }) {
                H3({ style { headingStyle() } }) { Text("Ändringshistorik") }
                if (entries.isEmpty()) {
                    Div({ style { property("text-align","center"); property("padding","1.5rem"); property("color","var(--rp-muted)") } }) { Text("Inga ändringar registrerade.") }
                } else {
                    entries.forEach { e ->
                        val fieldIcon = when (e.fieldName) { "check_in_time" -> "fa-solid fa-right-to-bracket"; "check_out_time" -> "fa-solid fa-right-from-bracket"; else -> "fa-solid fa-pen" }
                        val fieldLabel = when (e.fieldName) { "check_in_time" -> "Incheckning"; "check_out_time" -> "Utcheckning"; else -> e.fieldName }
                        fun fmtT(v: String?): String { if (v == null) return "–"; val t = if (v.contains("T")) v.substringAfter("T").take(5) else v.take(5); return t.ifBlank { "–" } }
                        val ts = kotlin.js.Date(e.changedAt.toDouble()).let { "${it.getHours().toString().padStart(2,'0')}:${it.getMinutes().toString().padStart(2,'0')}" }
                        Div({ style { property("padding","0.75rem"); property("border-bottom","1px solid var(--rp-hl-med)"); property("font-size","0.85rem") } }) {
                            Div({ style { property("display","flex"); property("align-items","center"); property("gap","0.5rem"); property("flex-wrap","wrap") } }) {
                                FaIcon(fieldIcon, size = "0.9rem")
                                Span({ style { property("font-weight","700"); property("color","var(--rp-text)") } }) { Text(fieldLabel) }
                                Span({ style { property("color","var(--rp-muted)") } }) { Text(fmtT(e.oldValue)) }
                                FaIcon("fa-solid fa-arrow-right", size = "0.75rem")
                                Span({ style { property("font-weight","700"); property("color","var(--rp-ok-text)") } }) { Text(fmtT(e.newValue)) }
                            }
                            Div({ style { property("color","var(--rp-muted)"); property("margin-top","0.25rem"); property("font-size","0.78rem") } }) {
                                Text("${e.changedByEmail ?: "ID ${e.changedByUserId}"} • $ts")
                            }
                        }
                    }
                }
                Div({ style { property("text-align","right"); property("margin-top","1rem") } }) {
                    Button({ onClick { auditLog = null }; style { property("padding","0.6rem 1.2rem"); property("border","2px solid var(--rp-pine)"); property("background","transparent"); property("color","var(--rp-pine)"); property("border-radius","50px"); property("font-weight","700"); property("cursor","pointer") } }) { Text("Stäng") }
                }
            }
        }
    }
}

private fun pliggarCenterLabel(viewMode: PliggarViewMode, anchor: String): String = when (viewMode) {
    PliggarViewMode.DAG -> formatNiceDate(anchor)
    PliggarViewMode.VECKA -> {
        val ws = mondayOf(anchor)
        val we = addDays(ws, 6)
        "v.${weekNumber(ws)} · ${shortDate(ws)} – ${shortDate(we)}"
    }
    PliggarViewMode.MANAD -> {
        val parts = anchor.split("-")
        "${MONTH_NAMES[parts[1].toInt()-1].replaceFirstChar { it.uppercase() }} ${parts[0]}"
    }
    PliggarViewMode.AR -> anchor.take(4)
}

private fun stepAnchor(viewMode: PliggarViewMode, anchor: String, forward: Boolean, update: (String) -> Unit) {
    val sign = if (forward) 1 else -1
    update(when (viewMode) {
        PliggarViewMode.DAG -> addDays(anchor, sign)
        PliggarViewMode.VECKA -> addDays(mondayOf(anchor), 7 * sign)
        PliggarViewMode.MANAD -> addMonths(anchor, sign)
        PliggarViewMode.AR -> addYears(anchor, sign)
    })
}

@Composable
private fun PliggarViewSegmented(current: PliggarViewMode, isMobile: Boolean, onChange: (PliggarViewMode) -> Unit) {
    val modes = listOf(PliggarViewMode.DAG to "Dag", PliggarViewMode.VECKA to "Vecka", PliggarViewMode.MANAD to "Månad", PliggarViewMode.AR to "År")
    Div({ style {
        property("display","inline-flex"); property("background","var(--rp-overlay)")
        property("border-radius","12px"); property("padding","3px"); property("gap","2px")
    } }) {
        modes.forEach { (mode, label) ->
            val active = mode == current
            Button({
                onClick { onChange(mode) }
                style {
                    property("background", if (active) "var(--rp-surface)" else "transparent")
                    property("color", if (active) "var(--rp-pine)" else "var(--rp-subtle)")
                    property("border","none"); property("border-radius","9px")
                    property("padding", if (isMobile) "0.45rem 0.6rem" else "0.45rem 0.85rem")
                    property("cursor","pointer"); property("font-weight", if (active) "700" else "600")
                    property("font-size","0.85rem"); property("font-family","inherit")
                    if (active) property("box-shadow","0 1px 3px rgba(13,13,24,0.08)")
                }
            }) { Text(label) }
        }
    }
}

// ── Helper functions ──

private fun defaultExportFrom(): String {
    val d = kotlin.js.Date()
    val ms = d.getTime() - (730L * 24 * 60 * 60 * 1000)
    val t = kotlin.js.Date(ms)
    return "${t.getFullYear()}-${(t.getMonth() + 1).toString().padStart(2, '0')}-01"
}

private fun downloadFile(filename: String, content: String, mimeType: String) {
    val blob = js("new Blob([content], {type: mimeType})")
    val url = js("URL.createObjectURL(blob)") as String
    val a = kotlinx.browser.document.createElement("a")
    a.setAttribute("href", url)
    a.setAttribute("download", filename)
    a.asDynamic().click()
    js("URL.revokeObjectURL(url)")
}

// ── Style helpers ──

private fun org.jetbrains.compose.web.css.StyleScope.cardStyle() {
    property("background", "var(--rp-surface)"); property("border-radius","24px"); property("padding","1.5rem")
}

private fun org.jetbrains.compose.web.css.StyleScope.headingStyle() {
    property("margin","0 0 1rem 0"); property("font-size","1.3rem"); property("font-weight","800"); property("color","var(--rp-text)")
}

private fun org.jetbrains.compose.web.css.StyleScope.gridHeaderStyle(columns: String) {
    property("display","grid"); property("grid-template-columns",columns)
    property("gap","0.5rem"); property("padding","0.75rem 1rem")
    property("background","var(--rp-overlay)"); property("border-radius","14px"); property("margin-bottom","0.5rem")
    property("font-weight","700"); property("font-size","0.75rem"); property("color","var(--rp-subtle)")
    property("text-transform","uppercase"); property("letter-spacing","0.5px")
}

private fun org.jetbrains.compose.web.css.StyleScope.gridRowStyle(columns: String) {
    property("display","grid"); property("grid-template-columns",columns)
    property("gap","0.5rem"); property("padding","0.85rem 1rem"); property("align-items","center")
    property("border-bottom","1px solid var(--rp-hl-med)")
}

private fun org.jetbrains.compose.web.css.StyleScope.inputStyle() {
    property("padding","0.6rem 1rem"); property("border","2px solid var(--rp-hl-med)"); property("border-radius","14px")
    property("font-size","0.95rem"); property("font-weight","600"); property("outline","none")
}

private fun org.jetbrains.compose.web.css.StyleScope.labelStyle() {
    property("display","block"); property("font-weight","700"); property("font-size","0.75rem"); property("color","var(--rp-subtle)")
    property("text-transform","uppercase"); property("letter-spacing","0.5px"); property("margin-bottom","0.4rem")
}

private fun org.jetbrains.compose.web.css.StyleScope.overlayStyle() {
    property("position","fixed"); property("inset","0"); property("z-index","1000")
    property("background","rgba(0,0,0,0.45)"); property("display","flex")
    property("align-items","center"); property("justify-content","center")
}

private fun org.jetbrains.compose.web.css.StyleScope.modalCardStyle() {
    property("background", "var(--rp-surface)"); property("border-radius","24px")
    property("padding","2rem"); property("width","min(500px, 90vw)")
    property("max-height","90vh"); property("overflow-y","auto")
}

private fun org.jetbrains.compose.web.css.StyleScope.smallBtnStyle(color: String) {
    property("padding","0.35rem 0.6rem"); property("border","none")
    property("background","var(--rp-overlay)"); property("border-radius","10px")
    property("font-size","0.8rem"); property("cursor","pointer")
}
